/**
 * Address and range arithmetic, without `node:net` (the dashboard imports this
 * package too).
 *
 * Section 8.8 keeps asking the same two questions — is this peer inside Cloudflare's
 * ranges, is this trusted entry inside a private one — and the API asks a third when
 * it checks a fetched list: does this range overlap anything reserved. `BlockList`
 * answers the first for an address but not the others for a range, so ranges are
 * compared here as integers: a family, the first address and the prefix length.
 *
 * Nothing here accepts a zone (`fe80::1%eth0`) or a hostname. Everything that feeds
 * these functions has been through `IpAddress` or `Cidr` first, or comes from
 * `/proc/net/tcp`, which prints neither.
 */
const bits = (family) => (family === 4 ? 32 : 128);
function parseIPv4(text) {
    const parts = text.split('.');
    if (parts.length !== 4)
        return undefined;
    let value = 0n;
    for (const part of parts) {
        if (!/^\d{1,3}$/.test(part))
            return undefined;
        const octet = Number(part);
        if (octet > 255 || (part.length > 1 && part.startsWith('0')))
            return undefined;
        value = (value << 8n) | BigInt(octet);
    }
    return value;
}
function parseIPv6(text) {
    if (text.includes('%'))
        return undefined;
    let head = text;
    let tail = '';
    const gap = text.indexOf('::');
    if (gap !== -1) {
        if (text.includes('::', gap + 1))
            return undefined;
        head = text.slice(0, gap);
        tail = text.slice(gap + 2);
    }
    const groups = (part, endsAddress) => {
        if (part === '')
            return [];
        const out = [];
        const pieces = part.split(':');
        for (const [index, piece] of pieces.entries()) {
            // An embedded IPv4 address may only end the address (`::ffff:192.0.2.1`).
            if (piece.includes('.') && endsAddress && index === pieces.length - 1) {
                const v4 = parseIPv4(piece);
                if (v4 === undefined)
                    return undefined;
                out.push(Number(v4 >> 16n), Number(v4 & 0xffffn));
                continue;
            }
            if (!/^[0-9a-fA-F]{1,4}$/.test(piece))
                return undefined;
            out.push(Number.parseInt(piece, 16));
        }
        return out;
    };
    const before = groups(head, gap === -1);
    const after = groups(tail, true);
    if (before === undefined || after === undefined)
        return undefined;
    const missing = 8 - before.length - after.length;
    if (gap === -1 ? missing !== 0 : missing < 1)
        return undefined;
    const all = [...before, ...new Array(gap === -1 ? 0 : missing).fill(0), ...after];
    let value = 0n;
    for (const group of all)
        value = (value << 16n) | BigInt(group);
    return value;
}
/** An address as a family and an integer, or undefined for anything else. */
export function parseAddress(text) {
    const trimmed = text.trim();
    if (trimmed.includes(':')) {
        const value = parseIPv6(trimmed);
        return value === undefined ? undefined : { family: 6, value };
    }
    const value = parseIPv4(trimmed);
    return value === undefined ? undefined : { family: 4, value };
}
/**
 * IPv4 written inside IPv6 (`::ffff:198.51.100.7`) is the IPv4 address. Sockets
 * print a dual-stack listener's IPv4 peers that way, and a Cloudflare IPv4 range
 * has to match them.
 */
function unmapped(address) {
    if (address.family === 6 && address.value >> 32n === 0xffffn) {
        return { family: 4, value: address.value & 0xffffffffn };
    }
    return address;
}
/** `a.b.c.d/p`, `x::/p`, or a bare address as the range of just itself. */
export function parseRange(text) {
    const slash = text.lastIndexOf('/');
    const address = parseAddress(slash === -1 ? text : text.slice(0, slash));
    if (address === undefined)
        return undefined;
    const width = bits(address.family);
    let prefix = width;
    if (slash !== -1) {
        const written = text.slice(slash + 1);
        if (!/^\d{1,3}$/.test(written))
            return undefined;
        prefix = Number(written);
        if (prefix > width)
            return undefined;
    }
    const hostBits = BigInt(width - prefix);
    return { family: address.family, first: (address.value >> hostBits) << hostBits, prefix };
}
const lastOf = (range) => range.first + (1n << BigInt(bits(range.family) - range.prefix)) - 1n;
/** Whether `address` is inside `range`. Unparseable input is never inside anything. */
export function rangeContains(range, address) {
    const parsed = typeof range === 'string' ? parseRange(range) : range;
    const raw = parseAddress(address);
    if (parsed === undefined || raw === undefined)
        return false;
    const candidate = unmapped(raw);
    if (candidate.family !== parsed.family)
        return false;
    return candidate.value >= parsed.first && candidate.value <= lastOf(parsed);
}
/** Whether every address of `inner` is inside `outer`. */
export function rangeWithin(inner, outer) {
    const a = parseRange(inner);
    const b = parseRange(outer);
    if (a === undefined || b?.family !== a.family)
        return false;
    return a.prefix >= b.prefix && a.first >= b.first && lastOf(a) <= lastOf(b);
}
/** Whether the two ranges share any address. */
export function rangesOverlap(left, right) {
    const a = parseRange(left);
    const b = parseRange(right);
    if (a === undefined || b?.family !== a.family)
        return false;
    return a.first <= lastOf(b) && b.first <= lastOf(a);
}
/** Whether `address` is inside any of `ranges`. */
export function anyRangeContains(ranges, address) {
    return ranges.some((range) => rangeContains(range, address));
}
function formatIPv4(value) {
    return [24n, 16n, 8n, 0n].map((shift) => String((value >> shift) & 0xffn)).join('.');
}
/** RFC 5952: lower case, the longest run of two or more zero groups as `::`. */
function formatIPv6(value) {
    const groups = Array.from({ length: 8 }, (_, i) => Number((value >> BigInt(112 - i * 16)) & 0xffffn));
    let bestStart = -1;
    let bestLength = 0;
    for (let i = 0; i < 8;) {
        if (groups[i] !== 0) {
            i += 1;
            continue;
        }
        let j = i;
        while (j < 8 && groups[j] === 0)
            j += 1;
        if (j - i > bestLength) {
            bestStart = i;
            bestLength = j - i;
        }
        i = j;
    }
    const hex = groups.map((group) => group.toString(16));
    if (bestLength < 2)
        return hex.join(':');
    return `${hex.slice(0, bestStart).join(':')}::${hex.slice(bestStart + bestLength).join(':')}`;
}
/**
 * One spelling per range: host bits cleared, IPv6 per RFC 5952, and always a
 * prefix — `AllowlistRange` requires one, so a lone address becomes `/32` or `/128`.
 * IPv4 written inside IPv6 is written as IPv4.
 */
export function normalizeCidr(text) {
    const parsed = parseRange(text);
    if (parsed === undefined)
        return undefined;
    if (parsed.family === 6 && parsed.prefix >= 96 && parsed.first >> 32n === 0xffffn) {
        return `${formatIPv4(parsed.first & 0xffffffffn)}/${String(parsed.prefix - 96)}`;
    }
    const address = parsed.family === 4 ? formatIPv4(parsed.first) : formatIPv6(parsed.first);
    return `${address}/${String(parsed.prefix)}`;
}
/** A peer as it will be written in a list: its own `/32` or `/128`. */
export function hostRange(address) {
    const raw = parseAddress(address);
    if (raw === undefined)
        return undefined;
    const { family, value } = unmapped(raw);
    return `${family === 4 ? formatIPv4(value) : formatIPv6(value)}/${family === 4 ? '32' : '128'}`;
}
/**
 * Addresses that are nobody's visitor and nobody's proxy on the internet: loopback,
 * the unspecified address, link-local and multicast. The same list the API's ban
 * checks call reserved.
 */
export const RESERVED_RANGES = [
    '0.0.0.0/8',
    '127.0.0.0/8',
    '169.254.0.0/16',
    '224.0.0.0/4',
    '240.0.0.0/4',
    '::/128',
    '::1/128',
    'fe80::/10',
    'ff00::/8',
];
/**
 * Private networks: where a load balancer in the same data centre usually sits
 * (section 8.8), and never a Cloudflare address. RFC 1918, carrier-grade NAT and
 * IPv6 unique local addresses.
 */
export const PRIVATE_RANGES = [
    '10.0.0.0/8',
    '172.16.0.0/12',
    '192.168.0.0/16',
    '100.64.0.0/10',
    'fc00::/7',
];
export const LOOPBACK_RANGES = ['127.0.0.0/8', '::1/128'];
export const isPrivateAddress = (address) => anyRangeContains(PRIVATE_RANGES, address);
export const isLoopbackAddress = (address) => anyRangeContains(LOOPBACK_RANGES, address);
export const isReservedAddress = (address) => anyRangeContains(RESERVED_RANGES, address);
/** Neither private nor reserved: an address the internet can reach. */
export const isPublicAddress = (address) => parseAddress(address) !== undefined && !isPrivateAddress(address) && !isReservedAddress(address);
//# sourceMappingURL=cidr.js.map